import { Link } from "react-router-dom";
import { CheckCircle2, AlertTriangle, Phone, DollarSign, ArrowRight, XCircle } from "lucide-react";
import { SiteLayout } from "@/components/site/SiteLayout";
import { PageSEO } from "@/components/site/PageSEO";
import { ResourceHero } from "@/components/site/ResourceHero";
import { QuickAnswer } from "@/components/site/QuickAnswer";
import { ReviewedBy } from "@/components/site/ReviewedBy";
import { CTASection } from "@/components/site/CTASection";
import { brand, founder } from "@/data/site/brand";
import { stockImages } from "@/data/site/stockImages";

const lastReviewed = "2026-06-12";
const datePublished = "2026-06-12";

const QUICK_ANSWER =
  "Most legitimate tax resolution cases cost between $2,500 and $10,000 in professional fees, depending on the complexity, the amount owed, the number of unfiled returns, and whether the IRS has assigned a Revenue Officer. Simple installment agreements can be under $1,500; multi-year Offers in Compromise, payroll-tax workouts, or active levy/garnishment defense typically run $5,000–$10,000+. Beware of any firm promising a fixed price before reviewing your IRS transcripts, or quoting fees as a percentage of what they 'save' you — both are red flags.";

const priceRows: {
  service: string;
  typical: string;
  drivers: string;
}[] = [
  {
    service: "Free initial consultation",
    typical: "$0",
    drivers: "Should always be free — used to scope the case and pull IRS transcripts before quoting.",
  },
  {
    service: "IRS transcript pull & case diagnosis",
    typical: "$0 – $500",
    drivers: "Often bundled into the engagement fee. Required before any honest quote.",
  },
  {
    service: "Simple installment agreement (1–2 years owed, no levy)",
    typical: "$750 – $2,500",
    drivers: "Balance owed, whether streamlined or full financial disclosure required.",
  },
  {
    service: "Currently Not Collectible (CNC / Status 53)",
    typical: "$1,500 – $3,500",
    drivers: "Form 433-A or 433-F preparation, hardship documentation, ACS or Revenue Officer negotiation.",
  },
  {
    service: "Penalty abatement (First-Time or reasonable cause)",
    typical: "$750 – $2,500",
    drivers: "First-Time Abatement is straightforward; reasonable cause requires a written narrative and supporting documents.",
  },
  {
    service: "Offer in Compromise (OIC)",
    typical: "$3,500 – $8,500",
    drivers: "Number of years, business vs. individual, asset complexity, whether currently in collection action.",
  },
  {
    service: "Wage garnishment or bank levy release",
    typical: "$1,500 – $4,500",
    drivers: "Speed matters — bank levies have a 21-day window. Often paired with a longer resolution engagement.",
  },
  {
    service: "Unfiled returns — preparation",
    typical: "$350 – $1,200 per return",
    drivers: "1040, business returns (1120-S, 1065, 1120), Schedule C complexity, missing records.",
  },
  {
    service: "IRS audit representation (correspondence)",
    typical: "$1,500 – $4,000",
    drivers: "Number of issues, document substantiation effort.",
  },
  {
    service: "IRS audit representation (office/field)",
    typical: "$4,000 – $12,000+",
    drivers: "Schedule C/E, partnerships, S corps, expanded scope, Appeals likelihood.",
  },
  {
    service: "Payroll tax (941) workout",
    typical: "$5,000 – $15,000+",
    drivers: "Number of quarters, Trust Fund Recovery Penalty exposure, Revenue Officer involvement.",
  },
  {
    service: "Trust Fund Recovery Penalty defense (Form 4180)",
    typical: "$3,500 – $8,500",
    drivers: "Number of responsible-person interviews, allocation analysis, appeal of assessment.",
  },
  {
    service: "U.S. Tax Court litigation",
    typical: "Refer to tax attorney",
    drivers: "CPAs do not litigate (unless USTCP-admitted). Expect attorney hourly fees of $300–$600+/hr.",
  },
];

const greenFlags = [
  "Free transcript pull and case diagnosis before any fee quote.",
  "Flat fee tied to a defined scope of work — in writing.",
  "Direct access to a credentialed practitioner (CPA, EA, or attorney) — not just a 'case manager.'",
  "Engagement letter that lists exactly what is and isn't included.",
  "Refund policy if the engagement is terminated before work begins.",
  "Honest acknowledgment that some cases cannot be settled and that 'pennies on the dollar' isn't a guarantee.",
];

const redFlags = [
  '"Pennies on the dollar" guarantees before the firm has seen your transcripts.',
  "Fees quoted as a percentage of what you 'save' — illegal under Circular 230 §10.27 in most situations.",
  "High-pressure tactics during the first call (e.g., 'sign today or the price doubles').",
  "Salespeople with no credentials promising IRS outcomes.",
  "No engagement letter, or one that doesn't define the scope.",
  "Upfront fee collected before pulling transcripts or before assigning a credentialed practitioner.",
  "Public regulatory actions, FTC enforcement, or large numbers of BBB complaints.",
];

const cheaperPaths = [
  {
    label: "DIY — owe under $50,000 and current on filings",
    body: "The IRS Online Payment Agreement portal will set up a streamlined installment agreement in minutes. No professional fee required.",
  },
  {
    label: "DIY — First-Time Penalty Abatement",
    body: "If you have a clean prior 3-year history, call the IRS at 800-829-1040 and request FTA on a single penalty period. The IRS grants it on the phone if you qualify.",
  },
  {
    label: "Low Income Taxpayer Clinic (LITC)",
    body: "If your household income is at or below 250% of the federal poverty level, an LITC may represent you for free. Find one at taxpayeradvocate.irs.gov/litc.",
  },
  {
    label: "Taxpayer Advocate Service (TAS)",
    body: "Free IRS-internal advocate for taxpayers facing economic hardship or IRS process failures. Call 877-777-4778.",
  },
];

const HowMuchDoesTaxResolutionCost = () => {
  const jsonLd: object[] = [
    {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: "How Much Does Tax Resolution Cost? CPA Pricing Guide (2026)",
      description: QUICK_ANSWER.slice(0, 200),
      datePublished,
      dateModified: lastReviewed,
      author: {
        "@type": "Person",
        name: founder.name,
        jobTitle: founder.title,
        url: `${brand.siteUrl}/about/franklin-sofi`,
      },
      reviewedBy: {
        "@type": "Person",
        name: founder.name,
        jobTitle: founder.title,
        url: `${brand.siteUrl}/about/franklin-sofi`,
      },
      publisher: {
        "@type": "Organization",
        name: brand.legalName,
        url: brand.siteUrl,
      },
      mainEntityOfPage: `${brand.siteUrl}/resources/how-much-does-tax-resolution-cost`,
      inLanguage: "en-US",
      speakable: {
        "@type": "SpeakableSpecification",
        cssSelector: [".speakable", "h1", "h2"],
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: [
        {
          "@type": "Question",
          name: "How much does tax resolution cost?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Most legitimate tax resolution cases cost $2,500 to $10,000 in professional fees, depending on complexity. Simple installment agreements can run under $1,500. Multi-year Offers in Compromise, payroll-tax workouts, and active levy defense typically cost $5,000–$10,000+.",
          },
        },
        {
          "@type": "Question",
          name: "How much does an Offer in Compromise cost?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Professional fees for an Offer in Compromise typically run $3,500 to $8,500. The IRS also charges a $205 application fee plus 20% of the lump-sum offer (both waived for low-income taxpayers).",
          },
        },
        {
          "@type": "Question",
          name: "Why do tax relief firms charge $5,000+?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "A real resolution case requires transcript analysis, Form 433 financial disclosure, IRS negotiation, and continuing case management — often 20–60+ hours of credentialed practitioner work. Fees below $2,000 for a complex case usually mean the work won't get done, or it'll be done by an unlicensed processor.",
          },
        },
        {
          "@type": "Question",
          name: "Is it illegal to charge a contingency fee for IRS work?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "IRS Circular 230 §10.27 generally prohibits contingent fees for tax services, with narrow exceptions (e.g., representation in connection with an IRS examination of an original return). Any firm quoting a fee as a percentage of what they 'save' you should be questioned carefully.",
          },
        },
        {
          "@type": "Question",
          name: "Can I do tax resolution myself?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Yes — for simple cases. If you owe under $50,000 and are current on filings, the IRS Online Payment Agreement portal will set up a streamlined installment agreement in minutes. First-Time Penalty Abatement can be granted over the phone. More complex cases (OIC, payroll tax, multi-year unfiled, active levy) usually justify professional representation.",
          },
        },
      ],
    },
  ];

  return (
    <SiteLayout>
      <PageSEO
        title="How Much Does Tax Resolution Cost? CPA Pricing Guide"
        description="Honest pricing ranges for IRS tax resolution — installment agreements, Offers in Compromise, audit defense, payroll tax, levy release — and how to spot overpriced firms."
        path="/resources/how-much-does-tax-resolution-cost"
        image={stockImages.countingCash.src}
        ogType="article"
        author={founder.name}
        publishedTime={datePublished}
        modifiedTime={lastReviewed}
        breadcrumbs={[
          { name: "Home", href: "/" },
          { name: "Resources", href: "/resources" },
          { name: "Tax Resolution Pricing", href: "/resources/how-much-does-tax-resolution-cost" },
        ]}
        jsonLd={jsonLd}
        speakableSelectors={[".speakable", ".faq-question", ".faq-answer"]}
      />

      <ResourceHero
        breadcrumbs={[
          { name: "Home", href: "/" },
          { name: "Resources", href: "/resources" },
          { name: "Tax Resolution Pricing" },
        ]}
        eyebrow="Pricing transparency"
        title="How much does tax resolution cost?"
        summary="Real fee ranges for every type of IRS resolution case, what drives the price, and the red flags that mean you're being overcharged. Written by a licensed CPA — not a salesperson."
        facts={[
          { label: "Last reviewed", value: "Jun 2026" },
          { label: "Reviewed by", value: founder.name },
          { label: "Read time", value: "7 min" },
        ]}
      />

      <section className="py-12 lg:py-16 bg-white">
        <div className="container mx-auto px-4 max-w-4xl">
          <QuickAnswer
            heading="How much does tax resolution actually cost?"
            answer={QUICK_ANSWER}
          />

          <ReviewedBy date={lastReviewed} />

          {/* Pricing table */}
          <h2 className="font-heading text-3xl font-bold text-navy-dark mt-12 mb-2">
            Typical fee ranges by service
          </h2>
          <p className="text-[15px] text-navy-dark/70 mb-6">
            These are honest, current ranges for credentialed (CPA, EA, or attorney) representation. Lower numbers in each range reflect simpler cases; higher numbers reflect complex multi-year, multi-entity, or active-collection situations.
          </p>
          <div className="overflow-x-auto rounded-xl border border-border">
            <table className="w-full text-sm">
              <thead className="bg-secondary/60 text-left">
                <tr>
                  <th className="p-4 font-semibold text-navy-dark">Service</th>
                  <th className="p-4 font-semibold text-navy-dark whitespace-nowrap">Typical fee</th>
                  <th className="p-4 font-semibold text-navy-dark">What drives the price</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {priceRows.map((row) => (
                  <tr key={row.service} className="align-top">
                    <td className="p-4 font-semibold text-navy-dark">{row.service}</td>
                    <td className="p-4 text-gold-dark font-semibold whitespace-nowrap">{row.typical}</td>
                    <td className="p-4 text-navy-dark/85">{row.drivers}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-navy-dark/60 mt-3">
            Fees are illustrative ranges based on the experience of a small CPA firm. Your case may fall outside these ranges. A real quote requires pulling your IRS transcripts first.
          </p>

          {/* IRS fees */}
          <h2 className="font-heading text-3xl font-bold text-navy-dark mt-16 mb-4">
            IRS fees you'll also pay
          </h2>
          <p className="text-[16px] text-navy-dark/85 leading-relaxed mb-4">
            Professional fees are separate from what the IRS charges directly. Common IRS user fees:
          </p>
          <ul className="space-y-2 text-[15px] text-navy-dark/85">
            <li className="flex gap-2.5"><DollarSign className="w-4 h-4 text-gold-dark mt-1 flex-shrink-0" /><span><strong>Installment agreement setup:</strong> $22 (online direct debit) up to $225 (mail-in non-direct-debit). Reduced or waived for low-income.</span></li>
            <li className="flex gap-2.5"><DollarSign className="w-4 h-4 text-gold-dark mt-1 flex-shrink-0" /><span><strong>Offer in Compromise application:</strong> $205 application fee + 20% lump-sum payment (both waived under low-income certification).</span></li>
            <li className="flex gap-2.5"><DollarSign className="w-4 h-4 text-gold-dark mt-1 flex-shrink-0" /><span><strong>Reinstating a defaulted IA:</strong> $89.</span></li>
            <li className="flex gap-2.5"><DollarSign className="w-4 h-4 text-gold-dark mt-1 flex-shrink-0" /><span><strong>Lien withdrawal or discharge:</strong> No fee for withdrawal; lien-discharge applications are free but may require an appraisal.</span></li>
          </ul>

          {/* Green flags / red flags */}
          <div className="grid md:grid-cols-2 gap-6 mt-16">
            <div className="rounded-2xl border-2 border-gold/40 bg-gold/5 p-6 lg:p-8">
              <div className="flex items-center gap-2 mb-3">
                <CheckCircle2 className="w-6 h-6 text-gold-dark" />
                <h2 className="font-heading text-xl font-bold text-navy-dark m-0">
                  Green flags
                </h2>
              </div>
              <ul className="space-y-2.5 mt-3">
                {greenFlags.map((g) => (
                  <li key={g} className="flex gap-2 text-[15px] text-navy-dark/85 leading-relaxed">
                    <CheckCircle2 className="w-4 h-4 text-gold-dark mt-1 flex-shrink-0" /><span>{g}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-2xl border-2 border-destructive/30 bg-destructive/5 p-6 lg:p-8">
              <div className="flex items-center gap-2 mb-3">
                <AlertTriangle className="w-6 h-6 text-destructive" />
                <h2 className="font-heading text-xl font-bold text-navy-dark m-0">
                  Red flags
                </h2>
              </div>
              <ul className="space-y-2.5 mt-3">
                {redFlags.map((r) => (
                  <li key={r} className="flex gap-2 text-[15px] text-navy-dark/85 leading-relaxed">
                    <XCircle className="w-4 h-4 text-destructive mt-1 flex-shrink-0" /><span>{r}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Why fees vary */}
          <h2 className="font-heading text-3xl font-bold text-navy-dark mt-16 mb-4">
            Why two firms quote different prices for the "same" case
          </h2>
          <p className="text-[16px] text-navy-dark/85 leading-relaxed">
            Tax resolution is not commoditized. The price difference between two firms usually reflects:
          </p>
          <ul className="space-y-2 mt-3 text-[15px] text-navy-dark/85">
            <li className="flex gap-2.5"><CheckCircle2 className="w-4 h-4 text-gold-dark mt-1 flex-shrink-0" /><span><strong>Who does the work.</strong> A CPA's billable hour costs more than a paralegal's — but a paralegal can't sign Form 2848 or negotiate an OIC.</span></li>
            <li className="flex gap-2.5"><CheckCircle2 className="w-4 h-4 text-gold-dark mt-1 flex-shrink-0" /><span><strong>What's actually included.</strong> A $2,000 "installment agreement package" often excludes transcript review, financial analysis, levy defense, and unfiled-return preparation — so the real bill ends up much higher.</span></li>
            <li className="flex gap-2.5"><CheckCircle2 className="w-4 h-4 text-gold-dark mt-1 flex-shrink-0" /><span><strong>The marketing tax.</strong> National "tax-relief" call centers spend $1,500–$3,000 of customer-acquisition cost per signed client. That cost shows up in your fee.</span></li>
            <li className="flex gap-2.5"><CheckCircle2 className="w-4 h-4 text-gold-dark mt-1 flex-shrink-0" /><span><strong>Honesty about your case.</strong> A firm that quotes you the maximum on day one will find ways to bill it. A firm that quotes the minimum will surprise you with addenda. The honest quote sits in the middle and is in writing.</span></li>
          </ul>

          {/* Cheaper paths */}
          <h2 className="font-heading text-3xl font-bold text-navy-dark mt-16 mb-4">
            When you don't need to pay a professional
          </h2>
          <p className="text-[16px] text-navy-dark/85 leading-relaxed mb-5">
            Not every IRS problem needs a paid representative. Real CPAs will tell you this on the first call:
          </p>
          <div className="space-y-3">
            {cheaperPaths.map((c) => (
              <div key={c.label} className="rounded-xl border border-border bg-white p-5">
                <p className="m-0 font-semibold text-navy-dark">{c.label}</p>
                <p className="m-0 mt-1 text-[15px] text-navy-dark/85 leading-relaxed">{c.body}</p>
              </div>
            ))}
          </div>

          {/* Bottom line */}
          <h2 className="font-heading text-3xl font-bold text-navy-dark mt-16 mb-4">
            The bottom line
          </h2>
          <p className="text-[16px] text-navy-dark/85 leading-relaxed">
            Real tax resolution costs real money, but it should never be a mystery. Ask any firm for a written engagement letter that lists exactly what's included, who's doing the work, and what triggers an additional fee. If they can't produce one — or won't pull your transcripts before quoting — keep looking. We're happy to be the second opinion: the first call is free.
          </p>

          {/* CTA */}
          <div className="mt-10 grid sm:grid-cols-2 gap-4">
            <Link to="/services" className="group flex items-center justify-between rounded-xl border border-border bg-white p-5 hover:border-gold hover:shadow-md transition">
              <span className="font-semibold text-navy-dark">Browse resolution services</span>
              <ArrowRight className="w-4 h-4 text-gold-dark group-hover:translate-x-1 transition" />
            </Link>
            <Link to="/resources/cpa-vs-tax-attorney-for-irs-help" className="group flex items-center justify-between rounded-xl border border-border bg-white p-5 hover:border-gold hover:shadow-md transition">
              <span className="font-semibold text-navy-dark">CPA vs. tax attorney</span>
              <ArrowRight className="w-4 h-4 text-gold-dark group-hover:translate-x-1 transition" />
            </Link>
          </div>

          <div className="mt-8 rounded-xl bg-navy-dark text-white p-6 lg:p-8 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-gold mb-1">
                Want a real quote — for free
              </p>
              <p className="m-0 text-white/90">
                We'll pull your IRS transcripts and tell you what your case will actually cost.
              </p>
            </div>
            <a
              href={`tel:${brand.phoneTel}`}
              className="inline-flex items-center justify-center gap-2 bg-gold hover:bg-gold-light text-navy-dark font-bold px-6 py-3 rounded-lg whitespace-nowrap"
            >
              <Phone className="w-4 h-4" /> {brand.phone}
            </a>
          </div>
        </div>
      </section>

      <CTASection />
    </SiteLayout>
  );
};

export default HowMuchDoesTaxResolutionCost;
